/**
 * 불러톡 인라인 패널 ($j.F.Admin.ChatPanel)
 *
 * 좌측 메뉴의 불러톡을 새창 대신 사이드바 옆에 삽입한다.
 *   [메뉴 | 본문] → 열면 [메뉴 | 불러톡 | 본문] (본문이 오른쪽으로 밀림)
 * 기존 채팅 페이지(/adm/chat/room·/adm/chat)를 iframe 으로 그대로 재사용한다.
 * 패널 마크업은 관리자 레이아웃(adm/layouts/main.blade.php)에 있고, 여닫기 onclick 이 이리로 온다.
 *
 * (구 public/js/admin.js 의 전역 openChatPanel/closeChatPanel/chatPanelBack/measureChatPanelVars 등)
 *
 * ⚠ measureVars 는 패널만 쓰는 게 아니다 — 좌측 상단 토스트($j.Ui.Toast)도 네비바 아래·사이드바
 *   오른쪽에 앉으려고 --adm-navh/--adm-sbw/--adm-vph 를 읽는다. 그래서 패널이 없어도 init 에서 잰다.
 */
export default class ChatPanel {
    constructor() {
        this._measureRaf = 0;
        // 리스너는 실제 함수 참조를 유지해야 iframe 문서에도 같은 핸들러를 붙인다.
        this._onEsc = this._onEsc.bind(this);
        this._onWheel = this._onWheel.bind(this);
        this._onResize = this._onResize.bind(this);
    }

    /**
     * 초기화 — 최초 실측 + 전역 리스너 부착. 관리자 부트스트랩이 매 페이지에서 부른다.
     * (팝업/bare 처럼 패널이 없는 화면에서도 토스트 위치용 변수는 재야 한다)
     */
    init() {
        this.measureVars();

        // 창 크기·글자크기가 바뀌면 실측값이 어긋난다. 드래그 중 resize 가 쏟아지므로 프레임당 한 번.
        // (글자크기 토글은 $j.F.Admin.Theme.apply() 가 resize 를 태워 여기로 들어온다)
        window.addEventListener('resize', this._onResize);

        // ESC 단계적 뒤로가기 · 본문 가로스크롤 변환 — 부모(최상위) 문서에만 한 번 건다.
        // iframe 안 문서는 onFrameLoad 에서 별도로 ESC 를 건다.
        if (window.self === window.top) {
            document.addEventListener('keydown', this._onEsc);
            document.addEventListener('wheel', this._onWheel, { passive: false });
        }

        // 패널 머리 버튼·iframe 로드 (main 레이아웃에만 있다). 처음 실리는 about:blank 는
        // 이보다 먼저 끝나지만 그때는 할 일이 없다 — 방목록을 열 때부터 받으면 된다
        $('#adm-chat-back').on('click', () => this.back());
        $('#adm-chat-panel .adm-chat-panel__close').on('click', () => this.close());
        $('#adm-chat-frame').on('load', () => this.onFrameLoad());
    }

    /**
     * 패널 위치 변수 갱신 — 네비바 높이/사이드바 폭/뷰포트 높이를 실측해 CSS 변수로 노출.
     *
     * --adm-vph 는 왜 있나 — 글자크기 3단계는 body 에 zoom 을 건다(admin/base/_fontsize.scss).
     * zoom 안에서 100vh 는 요소마다 다르게 풀려(일반흐름 vs position:fixed) 본문이 아래로 넘쳤다.
     * CSS 로 배율을 나누는 대신 JS 가 로컬 px 로 잰 뷰포트 높이를 넘겨 못을 박는다.
     */
    measureVars() {
        const nav  = document.querySelector('.main-header');
        const sb   = document.querySelector('.main-sidebar');
        const zoom = parseFloat(getComputedStyle(document.body).zoom) || 1;
        // 요소가 없으면 0 — 팝업/bare 레이아웃엔 네비바도 사이드바도 없다.
        document.body.style.setProperty('--adm-navh', (nav ? nav.offsetHeight : 0) + 'px');
        document.body.style.setProperty('--adm-sbw',  (sb  ? sb.offsetWidth  : 0) + 'px');
        document.body.style.setProperty('--adm-vph',  (window.innerHeight / zoom) + 'px');
    }

    /** 인라인 패널 열기/닫기 토글 */
    open() {
        const panel = document.getElementById('adm-chat-panel');
        if (!panel) return;
        if (document.body.classList.contains('chat-panel-open')) { this.close(); return; }
        const frame = document.getElementById('adm-chat-frame');
        // 열 때마다 목록부터 — iframe 내부 이동(방목록→방)은 src 를 안 바꿔 이전 방이 남는다.
        if (frame) frame.src = '/adm/chat/room';
        this.measureVars();
        document.body.classList.add('chat-panel-open');
    }

    /** 패널을 특정 방으로 바로 열기 */
    openRoom(rno) {
        const frame = document.getElementById('adm-chat-frame');
        if (!frame) return;
        frame.src = '/adm/chat?rno=' + rno;
        this.measureVars();
        document.body.classList.add('chat-panel-open');
    }

    /** 패널 닫기 (본문 원위치) */
    close() {
        document.body.classList.remove('chat-panel-open');
    }

    /** 패널 안에서 채팅방 → 방목록 으로 돌아가기 */
    back() {
        const frame = document.getElementById('adm-chat-frame');
        if (frame) frame.src = '/adm/chat/room';
    }

    /** 패널 iframe 이 채팅방(방목록 아님)을 보고 있는지 */
    isInRoom() {
        const frame = document.getElementById('adm-chat-frame');
        if (!frame) return false;
        try {
            const p = frame.contentWindow.location.pathname;
            return p.indexOf('/adm/chat') === 0 && p.indexOf('/room') === -1;
        } catch (e) { return false; }
    }

    /** iframe 로드 시: (1) 뒤로가기 버튼 노출 결정 (2) iframe 문서에도 ESC 핸들러 부착 */
    onFrameLoad() {
        const frame = document.getElementById('adm-chat-frame');
        const back  = document.getElementById('adm-chat-back');
        if (!frame || !back) return;
        back.hidden = !this.isInRoom();
        try {
            frame.contentDocument.addEventListener('keydown', this._onEsc);
        } catch (e) {}
    }

    /**
     * ESC 단계적 뒤로가기 — 부모/ iframe 어디서 눌러도 최상위 패널에 적용.
     *   채팅방 → 방목록 → 패널 닫기
     */
    _onEsc(e) {
        if (e.key !== 'Escape' && e.keyCode !== 27) return;
        try {
            const top = window.top;
            const cp = top.$j && top.$j.F && top.$j.F.Admin && top.$j.F.Admin.ChatPanel;
            if (!cp) return;
            if (!top.document.body.classList.contains('chat-panel-open')) return;
            if (cp.isInRoom()) cp.back();
            else cp.close();
        } catch (err) {}
    }

    /**
     * 패널이 열려 본문이 오른쪽으로 밀린 동안: 세로 휠 = 본문 가로 스크롤.
     *   - 닫혀 있으면 평소대로(세로) · 본문 위에서만(패널 iframe 제외) · 트랙패드 가로제스처·쉬프트+휠은 그대로.
     *   - 커서 아래 가장 가까운 가로 스크롤러가 본문(cw)일 때만 가로챈다.
     *     안쪽에 자체 스크롤 영역이 있거나(접수창 표 홀더) 본문이 가로로 넘치지 않으면
     *     기본 동작(세로 스크롤)에 맡긴다 — 가로채면 cw 는 안 움직이고 휠만 먹힌다.
     */
    _onWheel(e) {
        if (!document.body.classList.contains('chat-panel-open')) return;
        if (e.deltaY === 0) return;
        // 윈도우는 쉬프트+휠도 deltaY 로 온다(맥은 OS 가 deltaX 로 바꿔 줌). 브라우저 기본 가로스크롤에 맡긴다.
        if (e.shiftKey) return;
        const cw = document.querySelector('.content-wrapper');
        if (!cw || !cw.contains(e.target)) return;
        if (nearestScrollerX(e.target, cw) !== cw) return;
        cw.scrollLeft += e.deltaY;
        e.preventDefault();
    }

    _onResize() {
        if (this._measureRaf) return;
        this._measureRaf = requestAnimationFrame(() => { this._measureRaf = 0; this.measureVars(); });
    }
}

/** el 부터 root 까지 올라가며 처음 만나는 가로 스크롤 가능 요소. 없으면 null */
function nearestScrollerX(el, root) {
    for (; el && el.nodeType === 1; el = el.parentElement) {
        const ox = getComputedStyle(el).overflowX;
        if ((ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth) return el;
        if (el === root) break;
    }
    return null;
}
